import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useUserContext } from "contexts/contextUser";
import { ImportApi } from "./ops";

const importRootKey = ["imports"] as const;

export const importKeys = {
  all: importRootKey,
  active: [...importRootKey, "active"] as const,
  job: (jobId: string) => [...importKeys.all, "job", jobId] as const,
  rejections: (jobId: string, page: number, pageSize: number) =>
    [...importKeys.all, "rejections", jobId, page, pageSize] as const,
};

export function useStartCountryInfoImportJob() {
  const { accessToken } = useUserContext();

  return useMutation({
    mutationFn: (file: File) => {
      if (!accessToken) throw new Error("Access token is required");
      return ImportApi.startCountryInfoImportJob(accessToken, file);
    },
  });
}

export function useStartCountryDemoImportJob() {
  const { accessToken } = useUserContext();

  return useMutation({
    mutationFn: (file: File) => {
      if (!accessToken) throw new Error("Access token is required");
      return ImportApi.startCountryDemoImportJob(accessToken, file);
    },
  });
}

export function useStartT1DIndexImportJob() {
  const { accessToken } = useUserContext();

  return useMutation({
    mutationFn: (file: File) => {
      if (!accessToken) throw new Error("Access token is required");
      return ImportApi.startT1DIndexImportJob(accessToken, file);
    },
  });
}

export function useCancelImportJob() {
  const { accessToken } = useUserContext();

  return useMutation({
    mutationFn: ({ jobId, reason }: { jobId: string; reason?: string }) => {
      if (!accessToken) throw new Error("Access token is required");
      return ImportApi.cancelImportJob(accessToken, jobId, reason);
    },
  });
}

type UseImportJobStatusOptions = {
  pollIntervalMs?: number;
  timeoutMs?: number;
};

export function useImportJobStatus(
  jobId?: string,
  enabled = true,
  options: UseImportJobStatusOptions = {}
) {
  const { accessToken } = useUserContext();
  const { pollIntervalMs = 5000, timeoutMs = 15 * 60 * 1000 } = options;
  const startedAtRef = useRef<number | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    if (!jobId || !enabled) {
      startedAtRef.current = null;
      setIsTimedOut(false);
      return;
    }
    startedAtRef.current = Date.now();
    setIsTimedOut(false);
  }, [jobId, enabled]);

  const query = useQuery({
    queryKey: importKeys.job(jobId || ""),
    queryFn: ({ signal }) => {
      if (!jobId) throw new Error("jobId is required");
      if (!accessToken) throw new Error("Access token is required");
      return ImportApi.getImportJobStatus(accessToken, jobId, signal);
    },
    enabled: enabled && Boolean(jobId) && Boolean(accessToken),
    refetchInterval: (query) => {
      if (!startedAtRef.current) {
        startedAtRef.current = Date.now();
      }

      if (Date.now() - startedAtRef.current >= timeoutMs) {
        setIsTimedOut(true);
        return false;
      }

      const job = query.state.data;
      if (!job?.status) return pollIntervalMs;
      if (!job.finishedAt) return pollIntervalMs;
      return false;
    },
  });

  return {
    ...query,
    isTimedOut,
  };
}

type UseImportJobRejectionsOptions = {
  page?: number;
  pageSize?: number;
  enabled?: boolean;
};

export function useImportJobRejections(
  jobId?: string,
  options: UseImportJobRejectionsOptions = {}
) {
  const { accessToken } = useUserContext();
  const { page = 1, pageSize = 50, enabled = true } = options;

  return useQuery({
    queryKey: importKeys.rejections(jobId || "", page, pageSize),
    queryFn: ({ signal }) => {
      if (!jobId) throw new Error("jobId is required");
      if (!accessToken) throw new Error("Access token is required");
      return ImportApi.getImportJobRejections(accessToken, jobId, { page, pageSize }, signal);
    },
    enabled: enabled && Boolean(jobId) && Boolean(accessToken),
  });
}
